import { Link } from 'react-router-dom';
import { MessageSquare, ArrowRight, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { WorkOrder } from '@/types/maintenance';

interface Props {
  workOrders: WorkOrder[];
}

export function HandoffNotesCard({ workOrders }: Props) {
  const withHandoff = workOrders.filter(
    wo => wo.handoff_notes && wo.status !== 'closed',
  );

  if (withHandoff.length === 0) return null;

  return (
    <div className="bg-card rounded-lg border border-border/50">
      <div className="px-4 py-2.5 border-b border-border/50 flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold">Shift Handoff Notes</h2>
        <span className="ml-auto text-xs text-muted-foreground">{withHandoff.length}</span>
      </div>
      <div className="p-3 space-y-0.5">
        {withHandoff.map(wo => (
          <Link
            key={wo.id}
            to={`/maintenance/work-orders/${wo.id}`}
            className="flex items-start gap-3 px-2 py-2 rounded-md hover:bg-muted/50 transition-colors group"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="font-semibold text-sm">{wo.mold?.mold_id || wo.mold_id}</span>
                {wo.handed_off_at && (
                  <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground/50">
                    <Clock className="h-2.5 w-2.5" />
                    {formatDistanceToNow(new Date(wo.handed_off_at), { addSuffix: true })}
                  </span>
                )}
              </div>
              {(() => {
                try {
                  const parsed = JSON.parse(wo.handoff_notes ?? '') as { categories?: Array<{id: string; text: string}>; notes?: string };
                  if (Array.isArray(parsed.categories) && parsed.categories.length > 0) {
                    return (
                      <p className="text-[11px] text-muted-foreground line-clamp-2">
                        {parsed.categories.map(c => c.text || c.id).join(' · ')}
                        {parsed.notes ? ` · ${parsed.notes}` : ''}
                      </p>
                    );
                  }
                } catch { /* plain text */ }
                return <p className="text-[11px] text-muted-foreground line-clamp-2">{wo.handoff_notes}</p>;
              })()}
            </div>
            <ArrowRight className="h-3 w-3 text-muted-foreground/40 shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>
        ))}
      </div>
    </div>
  );
}
